import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Generates a professional PDF report for Merkle Tree Inclusion Proof
 * @param {Object} proofData - The proof verification data
 * @param {string} proofData.fileName - Name of the log file
 * @param {string} proofData.leafHash - Leaf hash value
 * @param {string} proofData.rootHash - Root hash value
 * @param {number} proofData.leafIndex - Index of the leaf in the tree
 * @param {number} proofData.leafCount - Total number of leaves
 * @param {Array<string>} proofData.inclusionPath - Array of hash values in the inclusion path
 * @param {boolean} proofData.verified - Verification result
 * @param {string} proofData.timestamp - Timestamp of verification
 * @param {string} proofData.transactionId - Blockchain transaction ID (optional)
 */
export const generateProofPDF = (proofData) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4"
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;

  // Color scheme
  const primaryColor = [88, 86, 214]; // #5856D6
  const successColor = [52, 199, 89]; // #34C759
  const errorColor = [255, 59, 48]; // #FF3B30
  const textDark = [28, 28, 30]; // #1C1C1E
  const textGray = [142, 142, 147]; // #8E8E93
  const bgLight = [242, 242, 247]; // #F2F2F7

  let yPosition = margin;

  // ==================== HEADER ====================
  // Company Logo/Title
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 35, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("Merkle Tree Inclusion Proof", pageWidth / 2, 15, { align: "center" });

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Cryptographic Verification Report", pageWidth / 2, 23, { align: "center" });

  yPosition = 45;

  // ==================== VERIFICATION STATUS BADGE ====================
  const statusText = proofData.verified ? "VERIFIED" : "VERIFICATION FAILED";
  const statusColor = proofData.verified ? [76, 175, 80] : errorColor; // Material Design Green

  doc.setFillColor(...statusColor);
  doc.roundedRect(margin, yPosition, contentWidth, 15, 3, 3, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(statusText, pageWidth / 2, yPosition + 10, { align: "center" });

  yPosition += 25;

  // ==================== DOCUMENT INFO ====================
  doc.setFillColor(...bgLight);
  doc.roundedRect(margin, yPosition, contentWidth, 25, 2, 2, "F");

  doc.setTextColor(...textDark);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  const infoStartY = yPosition + 7;
  doc.text("Document Information", margin + 5, infoStartY);

  doc.setFontSize(9);
  doc.setTextColor(...textGray);
  doc.text(`Report Generated: ${proofData.timestamp || new Date().toLocaleString()}`, margin + 5, infoStartY + 6);
  doc.text(`Log File: ${proofData.fileName || "N/A"}`, margin + 5, infoStartY + 12);
  if (proofData.transactionId) {
    doc.text(`Transaction ID: ${truncateHash(proofData.transactionId, 40)}`, margin + 5, infoStartY + 18);
  }

  yPosition += 35;

  // ==================== MERKLE TREE PARAMETERS ====================
  doc.setTextColor(...textDark);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Merkle Tree Parameters", margin, yPosition);

  yPosition += 8;

  const parametersData = [
    ["Leaf Index", proofData.leafIndex?.toString() || "N/A"],
    ["Total Leaf Count", proofData.leafCount?.toString() || "N/A"],
    // ["Tree Height", proofData.inclusionPath ? proofData.inclusionPath.length.toString() : "N/A"],
    ["Inclusion Path Length", proofData.inclusionPath ? proofData.inclusionPath.length.toString() : "N/A"]
  ];

  autoTable(doc, {
    startY: yPosition,
    head: [["Parameter", "Value"]],
    body: parametersData,
    theme: "striped",
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: "bold"
    },
    bodyStyles: {
      textColor: textDark,
      fontSize: 9
    },
    alternateRowStyles: {
      fillColor: bgLight
    },
    margin: { left: margin, right: margin }
  });

  yPosition = doc.lastAutoTable.finalY + 10;

  // ==================== CRYPTOGRAPHIC HASHES ====================
  doc.setTextColor(...textDark);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Cryptographic Hashes", margin, yPosition);

  yPosition += 8;

  // Leaf Hash
  doc.setFillColor(...bgLight);
  doc.roundedRect(margin, yPosition, contentWidth, 18, 2, 2, "F");

  doc.setTextColor(...textDark);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("Leaf Hash(Hash of Log File MetaData):", margin + 3, yPosition + 6);

  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...textGray);
  const leafHashLines = splitHashIntoLines(proofData.leafHash || "N/A", 80);
  leafHashLines.forEach((line, index) => {
    doc.text(line, margin + 3, yPosition + 12 + (index * 4));
  });

  yPosition += 18 + (leafHashLines.length > 1 ? 4 : 0) + 5;

  // Root Hash
  doc.setFillColor(...bgLight);
  doc.roundedRect(margin, yPosition, contentWidth, 18, 2, 2, "F");

  doc.setTextColor(...textDark);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("Root Hash(Merkle Tree Root Hash):", margin + 3, yPosition + 6);

  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...textGray);
  const rootHashLines = splitHashIntoLines(proofData.rootHash || "N/A", 80);
  rootHashLines.forEach((line, index) => {
    doc.text(line, margin + 3, yPosition + 12 + (index * 4));
  });

  yPosition += 18 + (rootHashLines.length > 1 ? 4 : 0) + 10;

  // ==================== INCLUSION PATH ====================
  if (proofData.inclusionPath && proofData.inclusionPath.length > 0) {
    // Check if we need a new page
    if (yPosition > pageHeight - 60) {
      doc.addPage();
      yPosition = margin;
    }

    doc.setTextColor(...textDark);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Inclusion Path (Merkle Proof)", margin, yPosition);

    yPosition += 8;

    const pathData = proofData.inclusionPath.map((hash, index) => [
      `Index ${index}`,
      hash || "N/A" // Show full hash without truncation
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [["Proof Index", "Hash Value"]],
      body: pathData,
      theme: "striped",
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: "bold"
      },
      bodyStyles: {
        textColor: textDark,
        fontSize: 7,
        font: "courier",
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: bgLight
      },
      margin: { left: margin, right: margin },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: contentWidth - 25, overflow: "linebreak" }
      }
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // ==================== VERIFICATION RESULT ====================
  // Check if we need a new page
  if (yPosition > pageHeight - 60) {
    doc.addPage();
    yPosition = margin;
  }

  // Draw verification result box
  const boxHeight = 40;
  const verificationBoxColor = proofData.verified ? [76, 175, 80] : errorColor; // Material Design Green
  
  doc.setFillColor(...verificationBoxColor);
  doc.setDrawColor(...verificationBoxColor);
  doc.roundedRect(margin, yPosition, contentWidth, boxHeight, 3, 3, "FD");

  // Add verification text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  const verificationText = proofData.verified ? "INCLUSION VERIFIED" : "VERIFICATION FAILED";
  doc.text(verificationText, pageWidth / 2, yPosition + 18, { align: "center" });

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  const subText = proofData.verified 
    ? "This document is cryptographically verified in the Merkle tree"
    : "Document could not be verified in the Merkle tree";
  doc.text(subText, margin + 35, yPosition + 28);

  yPosition += boxHeight + 10;

  // ==================== FOOTER ====================
  const totalPages = doc.internal.getNumberOfPages();
  
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Footer line
    doc.setDrawColor(...textGray);
    doc.setLineWidth(0.5);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

    // Footer text
    doc.setTextColor(...textGray);
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text(
      "This document contains cryptographic proof of data inclusion in a Merkle tree structure.",
      pageWidth / 2,
      pageHeight - 10,
      { align: "center" }
    );

    doc.setFont("helvetica", "normal");
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: "right" });
    doc.text("Generated by YSafe Audit System", margin, pageHeight - 10);
  }

  // ==================== SAVE PDF ====================
  const fileName = `Merkle_Proof_${proofData.fileName || "Report"}_${Date.now()}.pdf`;
  doc.save(fileName);
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Truncates a hash string for display
 * @param {string} hash - The hash string to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @return {string} Truncated hash with ellipsis in the middle
 */
const truncateHash = (hash, maxLength = 50) => {
  if (!hash) return "N/A";
  if (hash.length <= maxLength) return hash;
  const half = Math.floor(maxLength / 2) - 2;
  return `${hash.substring(0, half)}...${hash.substring(hash.length - half)}`;
};

/**
 * Splits a long hash into multiple lines
 * @param {string} hash - The hash string to split
 * @param {number} charsPerLine - Number of characters per line
 * @return {Array<string>} Array of hash string lines
 */
const splitHashIntoLines = (hash, charsPerLine = 80) => {
  if (!hash || hash === "N/A") return ["N/A"];
  const lines = [];
  for (let i = 0; i < hash.length; i += charsPerLine) {
    lines.push(hash.substring(i, i + charsPerLine));
  }
  return lines;
};

export default generateProofPDF;
